import React, { createRef, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme, theme } from '@app/theme';
import { Trans } from 'react-i18next';
import { SquareButton } from '@app/core';

const PANEL_SIZE = theme.marginBase * 36;
const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  page: {
    display: 'flex',
    height: '100%',
    alignItems: 'stretch',
    transition: 'transform .3s',
  },
  rightMenuOpened: {
    transform: `translateX(-${PANEL_SIZE}px)`,
    [theme.media.tablet]: {
      transform: 'translateX(0)',
    },
  },
  pageNavHiddenOnTablet: {
    [theme.media.tablet]: {
      display: 'block',
      height: 'auto',
    },
  },
  menu: {
    backgroundColor: theme.colors.grayBackground,
    padding: [theme.marginBase * 3, theme.marginBase * 4],

    width: PANEL_SIZE,

    display: 'flex',
    flexDirection: 'column',
  },
  nav: {
    [theme.media.tablet]: {
      paddingLeft: 0,
      paddingRight: 0,
      maxWidth: 72,

      alignItems: 'center',
    },
  },
  hiddenOnTablet: {
    [theme.media.tablet]: {
      display: 'none',
    },
  },
  backButton: {
    marginBottom: theme.marginBase * 3,
  },
  navTitle: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase * 3,

    [theme.media.tablet]: {
      display: 'none',
    },
  },
  mainContent: {
    flex: 1,
  },
  navList: {
    listStyle: 'none',
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: theme.colors.white,
  },
  scrollableContentNoScroll: {
    height: '100%',
    overflow: 'hidden',
  },
  contentNoScroll: {
    height: '100%',
  },
  horizontalNavPage: {
    [theme.media.tablet]: {
      display: 'block',
    },
  },
  horizontalNavList: {
    [theme.media.tablet]: {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  horizontalNav: {
    [theme.media.tablet]: {
      width: '100%',
      maxWidth: 'none',
      overflowX: 'auto',
      alignItems: 'center',
      flexFlow: 'row nowrap',
    },
  },
  extraRightMenu: {
    position: 'absolute',
    right: -PANEL_SIZE,
    top: 0,
    bottom: 0,
    display: 'none',
    overflow: 'auto',
  },
  extraRightMenuOpened: {
    display: 'block',
    zIndex: 100,

    [theme.media.tablet]: {
      transform: `translateX(-${PANEL_SIZE}px)`,
      minHeight: '100vh',
    },
    [theme.media.mobile]: {
      right: 0,
      left: 0,
      transform: `translateX(0)`,
      width: '100%',
    },
  },
}));

interface Props {
  children?: React.ReactNode;
  closeTo?: string;
  navTitle?: string;
  navTitleClassName?: string;
  menuItems?: React.ReactNode[];
  sideBarContent?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  contentFooter?: React.ReactNode;
  hideNavOnTablet?: boolean;
  horizontalNav?: boolean;
  navClassName?: string;
  scrollIndex?: number;
  fullpage?: boolean;
  noScroll?: boolean;
  extraRightMenu?: React.ReactNode;
  rightMenuOpened?: boolean;
}

export const SideMenuPage = ({
  children,
  closeTo,
  navTitle,
  navTitleClassName,
  menuItems,
  sideBarContent,
  footer,
  hideNavOnTablet,
  horizontalNav,
  navClassName,
  contentFooter,
  header,
  scrollIndex,
  noScroll,
  extraRightMenu,
  rightMenuOpened,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const menuItemsLength = menuItems ? menuItems.length : 0;
  const refs = useMemo(
    () =>
      Array.from({ length: menuItemsLength }, () => createRef<HTMLLIElement>()),
    [menuItemsLength],
  );
  useEffect(() => {
    if (scrollIndex && refs[scrollIndex]) {
      const li = refs[scrollIndex];
      setTimeout(() => {
        if (li && li.current) {
          li.current.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
            inline: 'center',
          });
        }
      }, 300);
    }
  }, [refs, scrollIndex]);

  return (
    <div
      className={classnames(classes.page, {
        [classes.horizontalNavPage]: horizontalNav,
        [classes.pageNavHiddenOnTablet]: hideNavOnTablet,
        [classes.rightMenuOpened]: rightMenuOpened,
      })}
    >
      <nav
        className={classnames(classes.nav, classes.menu, navClassName, {
          [classes.horizontalNav]: horizontalNav,
          [classes.hiddenOnTablet]: hideNavOnTablet,
        })}
      >
        {!!closeTo && (
          <SquareButton
            color="ink"
            overGray
            className={classes.backButton}
            to={closeTo}
            icon="ri-close-line"
          />
        )}
        {header}
        {navTitle && (
          <h1 className={classnames(classes.navTitle, navTitleClassName)}>
            {navTitle}
          </h1>
        )}
        <div className={classes.mainContent}>
          {!!menuItems && (
            <ul
              className={classnames(classes.navList, {
                [classes.horizontalNavList]: horizontalNav,
              })}
            >
              {menuItems.map((item, idx) => (
                <li key={idx} ref={refs[idx]}>
                  {item}
                </li>
              ))}
            </ul>
          )}
          {sideBarContent}
        </div>
        {footer}
      </nav>
      <div className={classes.contentContainer}>
        <div
          className={classnames(classes.scrollableContent, {
            [classes.scrollableContentNoScroll]: noScroll,
          })}
        >
          {children}
        </div>
        {contentFooter}
      </div>
      {extraRightMenu && (
        <div
          className={classnames(classes.extraRightMenu, classes.menu, {
            [classes.extraRightMenuOpened]: rightMenuOpened,
          })}
        >
          {extraRightMenu}
        </div>
      )}
    </div>
  );
};
